import { useState, useEffect } from 'react';
import { XIcon } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';

interface EditPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    productId: string;
    productName: string;
    regularPrice: number;
    proPlusPrice?: number;
    salePrice?: number;
    promotionalPeriod?: {
      startDate: string;
      endDate: string;
    };
  };
  onSave: (productId: string, updates: {
    regularPrice?: number;
    proPlusPrice?: number;
    salePrice?: number;
    promotionalPeriod?: {
      startDate: string;
      endDate: string;
    } | null;
  }) => Promise<void>;
}

// Função auxiliar para formatar data ISO para o formato do input date
const formatDateForInput = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  return new Date(isoDate).toISOString().split('T')[0];
};

export default function EditPriceModal({ isOpen, onClose, item, onSave }: EditPriceModalProps) {
  const [regularPrice, setRegularPrice] = useState(item.regularPrice?.toString() || '0');
  const [proPlusPrice, setProPlusPrice] = useState(item.proPlusPrice?.toString() || '');
  const [salePrice, setSalePrice] = useState(item.salePrice?.toString() || '');
  const [startDate, setStartDate] = useState(formatDateForInput(item.promotionalPeriod?.startDate));
  const [endDate, setEndDate] = useState(formatDateForInput(item.promotionalPeriod?.endDate));
  const [isPromoActive, setIsPromoActive] = useState(!!item.salePrice);

  // Atualizar os estados quando o item muda
  useEffect(() => {
    setRegularPrice(item.regularPrice?.toString() || '0');
    setProPlusPrice(item.proPlusPrice?.toString() || '');
    setSalePrice(item.salePrice?.toString() || '');
    setStartDate(formatDateForInput(item.promotionalPeriod?.startDate));
    setEndDate(formatDateForInput(item.promotionalPeriod?.endDate));
    setIsPromoActive(!!item.salePrice);
  }, [item]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const updates: {
      regularPrice?: number;
      proPlusPrice?: number;
      salePrice?: number;
      promotionalPeriod?: {
        startDate: string;
        endDate: string;
      } | null;
    } = {
      regularPrice: parseFloat(regularPrice),
    };

    if (proPlusPrice) {
      updates.proPlusPrice = parseFloat(proPlusPrice);
    }

    if (isPromoActive && salePrice && startDate && endDate) {
      updates.salePrice = parseFloat(salePrice);
      updates.promotionalPeriod = {
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString()
      };
    } else {
      updates.salePrice = undefined;
      updates.promotionalPeriod = null;
    }

    await onSave(item.productId, updates);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Editar Preços</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="font-medium mb-1">{item.productName}</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preço Regular
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={regularPrice}
              onChange={(e) => setRegularPrice(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preço PRO+
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={proPlusPrice}
              onChange={(e) => setProPlusPrice(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={isPromoActive}
                onChange={(e) => setIsPromoActive(e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Ativar Promoção</span>
            </label>

            {isPromoActive && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preço Promocional
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-primary focus:border-primary"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data Início
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data Fim
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
